import React, { useEffect, useState } from "react";
import { LdsButton, LdsTooltip, LdsLoadingSpinner } from "@elilillyco/ux-lds-react";
import { useSelector } from "react-redux";
import { getLoadingState as getServiceCatalogLoadingState } from "../../redux/slices/serviceCatalogSlice";
import { OrcaAPI } from "../../ApiServices/OrcaAPI";
import { addRequestCIL } from "../../redux/slices/cslSlice";
import { addRequest } from "../../redux/slices/requestsSlice";
import store from "../../redux/store";
import { Formik, Form, useFormikContext } from "formik";
import {OrcaScrollingCB,OrcaBrandSelect} from "../common/OrcaScrollingCB";
import { QuillTextArea, OrcaField, OrcaEffortField, OrcaTextArea, OrcaPriorityScore } from "../common/OrcaTextInput";
import { OrcaSelect, OrcaEnhancementSelect, OrcaGranularSelect, OrcaIndicationSelect, OrcaMetricSelect,OrcaBrandIndSelect,OrcaSprintSelect,OrcaHighlightCloudTagSelect} from "../common/OrcaSelect";
import { OrcaFormOptions } from "../common/OrcaFormOptions";
import { selectUserData } from "../../redux/slices/userSlice";
import { selectMlData, getMlData } from '../../redux/slices/mlSlice'; 
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
    title: Yup.string().required('Request Title is required').min(1),
    description: Yup.string().required('Request Description is required').min(1),
    business_unit: Yup.string().required('Business Unit is required').min(1),
    business_rationale: Yup.string().required('Business Rationale is required').min(1),
    request_brand: Yup.array().min(1, "Select at least one brand choice").of(Yup.string().required('Brand is required')).required('Brand is required'),
    requested_sprint: Yup.string().required('Requested Sprint is required').min(1),
    bv_category: Yup.string().required('Value Category is required').min(1),
    bv_priority: Yup.string().required('Value Priority is required').min(1),
    bv_urgency: Yup.string().required('Value Urgency is required').min(1),
    bv_impact: Yup.string().required('Value Impact is required').min(1),
    effort_enhancement_request: Yup.string().required('Effort Enhancement Request is required').min(1),
    effort_granular_request: Yup.string().required('Effort Granular Request is required').min(1),
    capability: Yup.string().required('Capability is required').min(1),
})

function ScrollToError() {
    const formik = useFormikContext();
    const submitting = formik?.isSubmitting;
    
    useEffect(() => {
        const el = document.querySelector('.lds-form-validation-error');
        (el?.parentElement ?? el)?.scrollIntoView({ behavior: 'smooth', block: 'center'});
    }, [submitting]);
    return null;
}

const formEmptyValues = {'actual_sprint': '', 'bia_metric_based_on': '','bia_trigger_based_on':'', 'bia_metrics': '', 'br_insight_graph': '', 'br_insight_text': '', 'br_insight_type': '', 'br_suggestion_text': '', 'br_text_type': '', 'brand': '', 'business_rationale': '', 'business_rule': '', 'business_unit': '', 'bv_calc_score': '2', 'bv_category': '', 'bv_impact': '', 'bv_priority': '', 'bv_urgency': '', 'capability': '', 'cardname': '', 'cloud_tag': '', 'ct_priority': '', 'description': '', 'effort_dtt_sfo': '', 'effort_enhancement_request': '', 'effort_granular_request': '', 'effort_o2': '', 'effort_omni_ml': '', 'effort_oppmodels_bia': '', 'effort_po_bia_ds': '', 'effort_po_sfo': '', 'fcap': '', 'highlight_cloud_tag': '', 'id': '', 'indication': '', 'insight_category': '', 'insight_graph_applicable_for_sleeve': '', 'insight_graph_brand_indication': '', 'insight_graph_data_source': '', 'insight_graph_display_format': '', 'insight_graph_display_section': '', 'insight_graph_entities_to_display': '', 'insight_graph_grain': '', 'insight_graph_graph_title': '', 'insight_graph_look_back': '', 'insight_graph_market_basket_class_name': '', 'insight_graph_metric_key': '', 'insight_graph_metric_name': '', 'insight_graph_metric_title': '', 'insight_graph_metric_to_display': '', 'insight_graph_source_filename': '', 'insight_graph_source_owner': '', 'insight_graph_time_based': '', 'insight_graph_type': '', 'insight_graph_x_label': '', 'insight_graph_y_label': '', 'insight_key': '', 'last_updated': '', 'metrics_file': '', 'new_metrics_ingest': '', 'other_metrics': '', 'refresh_freq': '', 'request_brand': [], 'request_key': '', 'requested_sprint': '', 'requester': '', 'sales_force_team': '', 'segment': '', 'status': '', 'ta_priority': '', 'title': '', 'trigger_criteria': '', 'urgency': ''}

export const RequestForm = ( {closeModal, formData, formDefaultValues, editable} ) => {
    let [ waitingSubmit, setWaitingSubmit ] = useState(false);
    let [ formValues, setFormValues ] = useState({...formDefaultValues, ...formData});
    let [ closingMessage, setClosingMessage ] = useState('')
    const [changeFormikValueFlag, setChangeFormikValueFlag] = useState(false);
    let serviceCatalogLoadingState = useSelector(getServiceCatalogLoadingState);

    const userData = useSelector(selectUserData);

    const location = useLocation();
    
    useEffect(() => {
        setFormValues({...formEmptyValues, ...formData})
        setChangeFormikValueFlag(!changeFormikValueFlag)
    }, [formData])

    function closeMessage() {
        setClosingMessage('')
        closeModal()
    }

    async function submitRequest(data) {
        setWaitingSubmit(true);
        let response = ''
        let new_row_id = null;
        if(data.capability == 'cloudtag_insight' || data.capability == 'br_insight'){
            data['insight_status'] = 'Request Submitted';
        } else {
            data['insight_status'] = '';
        }
        if (data['action'] === 'sendtojira') {
            data['status'] = 'Backlog';
            response = await OrcaAPI({ uri: "/jira_card", method: "POST", body: data });
            data['cardnumber'] = response.data.jira_card_number;
            new_row_id = response.data.new_row_id;
        } else {
            response = await OrcaAPI({ uri: "/submit_request", method: "PUT", body: data });
            new_row_id = response.data;
        }

        data['id'] = new_row_id;
        data['request_key'] = 'ORCA-' + new_row_id;
        setWaitingSubmit(false);
        setClosingMessage('Request submitted successfully')
        setTimeout(closeMessage, 3000)
        if (location.pathname.includes('/csl') && ["update", "view"].includes(formValues?.formType)) {
            store.dispatch(addRequestCIL(data))
        } else {
            store.dispatch(addRequest(data))
        } 
    }

    
    function handleSubmit(formValues) {
        // remove any fields on the form not related to the API
        if (!formValues['action'] || (formValues['action']!='save' && formValues['action']!='sendtojira')) {
            return
        }
        let temp = (({formType, action, actual_sprint, bia_metric_based_on,bia_trigger_based_on, bia_metrics, br_insight_graph, br_insight_text, br_insight_type, br_suggestion_text, br_text_type, brand, business_rationale, business_rule, business_unit, bv_calc_score, bv_category, bv_impact, bv_priority, bv_urgency, capability, cardname, cloud_tag, ct_priority, description, effort_dtt_sfo, effort_enhancement_request, effort_granular_request, effort_o2, effort_omni_ml, effort_oppmodels_bia, effort_po_bia_ds, effort_po_sfo, fcap, highlight_cloud_tag, id, indication, insight_category, insight_graph_applicable_for_sleeve, insight_graph_brand_indication, insight_graph_data_source, insight_graph_display_format, insight_graph_display_section, insight_graph_entities_to_display, insight_graph_grain, insight_graph_graph_title, insight_graph_look_back, insight_graph_market_basket_class_name, insight_graph_metric_key, insight_graph_metric_name, insight_graph_metric_title, insight_graph_metric_to_display, insight_graph_source_filename, insight_graph_source_owner, insight_graph_time_based, insight_graph_type, insight_graph_x_label, insight_graph_y_label, insight_key, last_updated, metrics_file, new_metrics_ingest, other_metrics, refresh_freq, request_brand, request_key, requested_sprint, requester, sales_force_team, segment, status, ta_priority, title, trigger_criteria, urgency}) => ({formType, action, actual_sprint, bia_metric_based_on,bia_trigger_based_on, bia_metrics, br_insight_graph, br_insight_text, br_insight_type, br_suggestion_text, br_text_type, brand, business_rationale, business_rule, business_unit, bv_calc_score, bv_category, bv_impact, bv_priority, bv_urgency, capability, cardname, cloud_tag, ct_priority, description, effort_dtt_sfo, effort_enhancement_request, effort_granular_request, effort_o2, effort_omni_ml, effort_oppmodels_bia, effort_po_bia_ds, effort_po_sfo, fcap, highlight_cloud_tag, id, indication, insight_category, insight_graph_applicable_for_sleeve, insight_graph_brand_indication, insight_graph_data_source, insight_graph_display_format, insight_graph_display_section, insight_graph_entities_to_display, insight_graph_grain, insight_graph_graph_title, insight_graph_look_back, insight_graph_market_basket_class_name, insight_graph_metric_key, insight_graph_metric_name, insight_graph_metric_title, insight_graph_metric_to_display, insight_graph_source_filename, insight_graph_source_owner, insight_graph_time_based, insight_graph_type, insight_graph_x_label, insight_graph_y_label, insight_key, last_updated, metrics_file, new_metrics_ingest, other_metrics, refresh_freq, request_brand, request_key, requested_sprint, requester, sales_force_team, segment, status, ta_priority, title, trigger_criteria, urgency}))(formValues);
        // filter out any empty fields
        let data = {}
        Object.keys(temp).map((key) => { if (temp[key] !== undefined && temp[key] !== null && temp[key]) { data[key] = temp[key]} });
        //let data = { ...formValues }
        if (Array.isArray(data['sales_force_team'])) {
            data['sales_force_team'] = data['sales_force_team'].join(';')
        }
        if (Array.isArray(data['insight_graph_applicable_for_sleeve'])) {
            data['insight_graph_applicable_for_sleeve'] = data['insight_graph_applicable_for_sleeve'].join(';')
        }
        if (Array.isArray(data['request_brand'])) {
            data['request_brand'] = data['request_brand'].join(';')
        }
        const requester = data['requester'] || userData.name
        data = { ...data, 'requester': requester, 'status': 'Draft'};

        // convert the quill delta object to a string for fields that have it
        for (let field of ['cloud_tag', 'br_insight_text', 'br_suggestion_text']) {
            if (formValues[`${field}_delta_obj`] != null) {
                data[field] = JSON.stringify(formValues[`${field}_delta_obj`]);
            }
        }     
        submitRequest(data);
    }

    return (
        <div className="box">
        { waitingSubmit && <LdsLoadingSpinner /> }
        { closingMessage && <><div>{closingMessage}</div></> }
        { !waitingSubmit && !closingMessage &&
        <Formik
            initialValues={formValues}
            key={changeFormikValueFlag}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                handleSubmit(values);
                setSubmitting(false);
            }}
            validationSchema={validationSchema}
            enableReinitialize
        >
            {({ values, handleSubmit, handleChange, errors, touched, setFieldValue }) => (
            
            <Form className="box" id="submitform" onSubmit={(e)=>{ handleSubmit(e); }}>
            {/*Fields common to all requests */}
            <OrcaField name="requester"  hidden disabled={!editable} />
            {location.pathname.includes('/csl') && ["update", "view"].includes(formValues?.formType) && <span className="more"><OrcaField name='insight_key' disabled /></span>}
            <OrcaField name='title' required disabled={!editable} />
            <OrcaTextArea name="description" required disabled={!editable} />
            <OrcaSelect name="business_unit" required disabled={!editable}/>
            <OrcaTextArea name="business_rationale" required disabled={!editable} />
{/*<OrcaScrollingCB name="request_brand" required disabled={!editable}/>*/}
            <OrcaBrandSelect name="request_brand" formlink="brand" required disabled={!editable}/>
            {/*<OrcaSelect name="requested_sprint" required disabled={!editable}/>*/}
            <OrcaSprintSelect name="requested_sprint" formlink="sprint" required disabled={!editable}/>
            <hr className='more' />
            
            {/*Business value calulation fields */}
            <h3 className='more' style={{marginBottom:'10px', marginTop:'10px'}}>Business Value Score</h3>
            <OrcaSelect name="bv_category" required disabled={!editable}/>
            <OrcaSelect name="bv_priority" required disabled={!editable}/>
            <OrcaSelect name="bv_urgency" required disabled={!editable}/>
            <OrcaSelect name="bv_impact" required disabled={!editable}/>
            <OrcaPriorityScore name="bv_calc_score" disabled />
            <hr className='more' />

            {/*Effort calculation fields*/}
            <h3 className='more' style={{marginBottom:'10px', marginTop:'10px'}}>Effort Estimation (FTE Weeks)</h3>
            { serviceCatalogLoadingState && <LdsLoadingSpinner style={{ alignItems:'center' }} size={70} animationSpeed={1500}/> }
            { !serviceCatalogLoadingState &&
                <>
                <OrcaEnhancementSelect name="effort_enhancement_request" required disabled={!editable} />
                <OrcaGranularSelect name="effort_granular_request" required disabled={!editable} />
                </>
            }

            <OrcaEffortField name="effort_po_sfo" label="PO-SFO" disabled />
            <OrcaEffortField name="effort_po_bia" label="PI-BI&A DS" disabled />
            <OrcaEffortField name="effort_dtt_sfo" label="DTT-SFO" disabled />
            <OrcaEffortField name="effort_o2" label="O2" disabled />
            <OrcaEffortField name="effort_oppmodels_bia" label="Opportunity Models - BI&A" disabled />
            <hr className='more' />

            {/*Capability specific fields*/}
            <h3 className='more' style={{marginBottom:'10px', marginTop:'10px'}}>Request Type</h3>
            <OrcaSelect name="capability" required disabled={!editable}/>
            <br />

            {/*CIL fiels*/}
            <CILFormFields values={values} editable={editable} setFieldValue={setFieldValue} formValues={formValues} setFormValues={setFormValues} />

            <hr className='more' />
            { editable && <LdsButton id="saveButton" value="save" label="save" name="submit" onClick={() => setFieldValue("action", "save")} classes={ userData.role === 'Admin' || userData.role === 'Requester' ? '' : 'more'} type='submit' >Save as Draft</LdsButton> }
            { editable && <LdsButton id="jiraButton" value="sendtojira" label="Send to Jira" name="submit" onClick={() => setFieldValue("action", "sendtojira")} type='submit'>Save and Send to Jira</LdsButton> }
            <ScrollToError />
            </Form>

        )}
        </Formik> 
        }
        </div>
    )
}

export function CILFormFields({formValues, values, editable, setFieldValue, setFormValues}) {
    const metricData = useSelector(selectMlData);
    function handleExistingMetricChange(event) {
        let metric = event.target.value;
        let selectedMetric = metricData.filter((option) => {
            return option.insight_graph_metric_key.toLowerCase() === metric.toLowerCase();
        });
        let data = { ...formValues, ...selectedMetric[0] };
        data.br_insight_graph = "yes";
        data.capability = "br_insight";
        data.insight_graph_type = "existing";
        setFormValues(data);
    }

    return (
    <>
        {// Cloud tag insights fields
            values.capability == 'cloudtag_insight' && <>
                <hr className='more' />
                <h3 className='more' style={{marginBottom:'10px', marginTop:'10px'}}>Cloud Tag Insight</h3>
                <OrcaField name="insight_key" hidden disabled={!editable}/>
                <OrcaField name="segment" disabled={!editable} />
                <OrcaSelect name="br_insight_type" disabled={!editable}/>
                <OrcaTextArea name="business_rule" disabled={!editable}  />
                <OrcaTextArea name="bia_metrics" disabled={!editable}  />
                <OrcaTextArea name="other_metrics" disabled={!editable} />
                <OrcaTextArea name="trigger_criteria" disabled={!editable} />
                <OrcaField name="metrics_file" disabled={!editable} />
                <OrcaField name="new_metrics_ingest" disabled={!editable} />
                <OrcaSelect name="refresh_freq" disabled={!editable}/>
                <OrcaField name="fcap" disabled={!editable} />
                <OrcaScrollingCB name="sales_force_team" disabled={!editable} />
                <OrcaBrandIndSelect name="brand" disabled={!editable}/>
                <OrcaIndicationSelect name="indication" formlink="brand" map={OrcaFormOptions['brandIndicationMap']} disabled={!editable} />
                <OrcaField name="ct_priority" disabled={!editable}/>
                <OrcaTextArea name="bia_metric_based_on" disabled={!editable} />
                <OrcaTextArea name="bia_trigger_based_on" disabled={!editable} />
                <QuillTextArea name="cloud_tag" disabled={!editable}/>
                <OrcaHighlightCloudTagSelect name="highlight_cloud_tag" onChange={(event) => setFieldValue('highlight_cloud_tag', event.target.value)} disabled={!editable}/>
                <OrcaMetricSelect name="insight_graph_metric_key" disabled={!editable} /> 
            </>
        }

        {// Business Rule Insight fields
            values.capability == 'br_insight' && <>
                <hr className='more' />
                <h3 className='more' style={{marginBottom:'10px', marginTop:'10px'}}>Business Rule Insight</h3>
                <OrcaField name="insight_key" hidden disabled={!editable}/>
                <div className="more">
                <OrcaSelect name="insight_category" disabled={!editable}/>
                </div>
                <OrcaField name="segment" disabled={!editable} />
                <OrcaSelect name="br_insight_type" disabled={!editable}/>
                <OrcaTextArea name="business_rule" disabled={!editable} />
                <OrcaTextArea name="bia_metrics" disabled={!editable} />
                <OrcaTextArea name="other_metrics" disabled={!editable} />
                <OrcaTextArea name="trigger_criteria" disabled={!editable} />
                <OrcaField name="metrics_file" disabled={!editable} />
                <OrcaField name="new_metrics_ingest" disabled={!editable} />
                <OrcaSelect name="refresh_freq" disabled={!editable}/>
                <OrcaSelect name="br_text_type" disabled={!editable}/>
                <QuillTextArea name="br_insight_text" disabled={!editable} />
                <QuillTextArea name="br_suggestion_text" disabled={!editable} />
                <OrcaField name="fcap" disabled={!editable} />
                <OrcaScrollingCB name="sales_force_team" disabled={!editable} />
                <OrcaBrandIndSelect name="brand" formlink="brand" disabled={!editable}/>
                <OrcaIndicationSelect name="indication" formlink="brand" map={OrcaFormOptions['brandIndicationMap']} disabled={!editable} />
                <OrcaSelect name="ta_priority" disabled={!editable}/>
                <OrcaSelect name="br_insight_graph" disabled={!editable}/>

            {// Business Rule Insight Graph fields
                values.br_insight_graph=='yes' && <>
                <br className="more"/>
                <hr className='more' />
                <h3 className='more' style={{marginBottom:'10px', marginTop:'10px'}}>Visual Metrics</h3>
                <OrcaSelect name="insight_graph_type" disabled={!editable}/>
                {values.insight_graph_type == "new" && <OrcaField name="insight_graph_metric_key" disabled={!editable} /> }
                {values.insight_graph_type == "existing" && <OrcaMetricSelect name="insight_graph_metric_key" disabled={!editable} onChange={handleExistingMetricChange} /> }
                <OrcaField name="insight_graph_metric_name" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaSelect name="insight_graph_display_section" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaSelect name="insight_graph_display_format" disabled={!editable || values.insight_graph_type == "existing"}/>
                <OrcaTextArea name="insight_graph_metric_to_display" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_market_basket_class_name" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_entities_to_display" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_source_owner" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_source_filename" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_data_source" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaBrandIndSelect name="insight_graph_brand" disabled={!editable || values.insight_graph_type == "existing"}/>
                <OrcaIndicationSelect name="insight_graph_indication" formlink="insight_graph_brand" map={OrcaFormOptions['brandIndicationMap']} disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_graph_title" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaSelect name="insight_graph_time_based" disabled={!editable || values.insight_graph_type == "existing"}/>
                <OrcaField name="insight_graph_x_label" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_y_label" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_grain" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaField name="insight_graph_look_back" disabled={!editable || values.insight_graph_type == "existing"} />
                <OrcaScrollingCB name="insight_graph_applicable_for_sleeve" disabled={!editable || values.insight_graph_type == "existing"} />
                <br />
            </>
            }
        </>
        }
    </>)
}
